import { FC, RefObject, useContext, useMemo } from "react";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { ServiceAreaEditorPane } from "./ServiceAreaEditorPane";
import { QESButton } from "@app/shared/qes-standards";
import { stateKey, zipKey } from "./types";
import { ServiceAreaEditorContext } from "./ServiceAreaEditorContext";

interface ServiceAreaEditorStatesProps {
    statePaneRef: RefObject<HTMLDivElement>;
}

const ServiceAreaEditorStates: FC<ServiceAreaEditorStatesProps> = (props: ServiceAreaEditorStatesProps) => {
    const editorContext = useContext(ServiceAreaEditorContext);

    const handleStateCheckboxChange = (stateAbbr: string) => {
        const updatedZipLookup = structuredClone(editorContext.zipLookup);
        const updatedCountyLookup = structuredClone(editorContext.countyLookup);
        const updatedStateLookup = structuredClone(editorContext.stateLookup);

        const countiesAffected = editorContext.stateToCountyLookup[stateAbbr] || [];

        /* Some counties don't have zip codes, but undefined > 0 returns false
            so they'll be filtered out before being iterated over
         */
        const zipsAffected = countiesAffected
            .filter((countySSA) =>
                editorContext.countyToZipLookup[countySSA]?.length > 0)
            .flatMap((countySSA) => editorContext.countyToZipLookup[countySSA]
                .map((zipCode) => zipKey(countySSA, zipCode)))
            || [];

        const wasStateChecked = editorContext.stateLookup[stateAbbr].checkboxState === CheckboxStateEnum.Checked;

        zipsAffected.forEach((zip) => {
            updatedZipLookup[zip].checkboxState = wasStateChecked ? CheckboxStateEnum.Unchecked : CheckboxStateEnum.Checked;
        });

        countiesAffected.forEach((countySSA) => {
            let newCountyStatus: RegionSetStatusType;
            if (wasStateChecked) {
                newCountyStatus = RegionSetStatusType.Unknown;
            } else {
                // if a county was already checked we should keep its old status
                const oldStatus = editorContext.countyLookup[countySSA].countyStatus;
                // otherwise make it Active
                newCountyStatus = !!oldStatus && oldStatus !== RegionSetStatusType.Unknown ? oldStatus : RegionSetStatusType.Active;
            }
            updatedCountyLookup[countySSA].countyStatus = newCountyStatus;
        });

        updatedStateLookup[stateAbbr].checkboxState = wasStateChecked ? CheckboxStateEnum.Unchecked : CheckboxStateEnum.Checked;

        editorContext.setStateLookup(updatedStateLookup);
        editorContext.setCountyLookup(updatedCountyLookup);
        editorContext.setZipLookup(updatedZipLookup);
    }

    const handleSelectAllStates = () => {
        // selecting all states means selecting all everything
        const updatedZipLookup = structuredClone(editorContext.zipLookup);
        const updatedCountyLookup = structuredClone(editorContext.countyLookup);
        const updatedStateLookup = structuredClone(editorContext.stateLookup);

        Object.keys(editorContext.stateLookup).forEach((stateAbbr) => {
            updatedStateLookup[stateAbbr].checkboxState = CheckboxStateEnum.Checked;
        });

        Object.keys(editorContext.countyLookup).forEach((countySSA) => {
            const oldStatus = editorContext.countyLookup[countySSA].countyStatus;
            const newStatus = !!oldStatus && oldStatus !== RegionSetStatusType.Unknown ? oldStatus : RegionSetStatusType.Active;
            updatedCountyLookup[countySSA].countyStatus = newStatus;
        });

        Object.keys(editorContext.zipLookup).forEach((zipCode) => {
            updatedZipLookup[zipCode].checkboxState = CheckboxStateEnum.Checked;
        });

        editorContext.setStateLookup(updatedStateLookup);
        editorContext.setCountyLookup(updatedCountyLookup);
        editorContext.setZipLookup(updatedZipLookup);
    }

    const handleClearAllStates = () => {
        // unselecting all states means unselecting all everything
        const updatedZipLookup = structuredClone(editorContext.zipLookup);
        const updatedCountyLookup = structuredClone(editorContext.countyLookup);
        const updatedStateLookup = structuredClone(editorContext.stateLookup);

        Object.keys(editorContext.stateLookup).forEach((stateAbbr) => {
            updatedStateLookup[stateAbbr].checkboxState = CheckboxStateEnum.Unchecked;
        });

        Object.keys(editorContext.countyLookup).forEach((countySSA) => {
            updatedCountyLookup[countySSA].countyStatus = RegionSetStatusType.Unknown;
        });

        Object.keys(editorContext.zipLookup).forEach((zipCode) => {
            updatedZipLookup[zipCode].checkboxState = CheckboxStateEnum.Unchecked;
        });

        editorContext.setStateLookup(updatedStateLookup);
        editorContext.setCountyLookup(updatedCountyLookup);
        editorContext.setZipLookup(updatedZipLookup);
    }

    const handleStateClick = (newStateAbbreviation: string) => {
        const oldStateAbbreviation = editorContext.selectedState;
        // we don't know the previous selectedState value in a useEffect hook,
        // so we'll clear the previous state's county checkboxes here
        const oldStateCountySSAs = editorContext.stateToCountyLookup[oldStateAbbreviation] || [];
        const updatedCountyLookup = structuredClone(editorContext.countyLookup);
        oldStateCountySSAs.forEach((ssa) => {
            updatedCountyLookup[ssa].checkboxState = CheckboxStateEnum.Unchecked;
        });

        editorContext.setSelectedState(newStateAbbreviation);
        editorContext.setCountyLookup(updatedCountyLookup);
    }

    const statesList = useMemo(() => {
        return editorContext.renderedStates.map((state) => {
            const isStateActive = state[stateKey] === editorContext.selectedState;
            // I think it's a little weird that clicking the label both selects the state and shows the list of counties,
            // but that's how it's been for the last 5 years so it's just expected now.
            // If that changes at any point, onLabelClick={(e) => e.preventDefault()} will stop the checkbox from being changed
            return (
                // It's also worth documenting that clicking the checkbox or label also counts as clicking the div here, and both/all
                // of those events will trigger from deepest to shallowest 
                <div
                    className={`service-area-editor__state-item${isStateActive ? " service-area-editor__state-item--active" : ""}`}
                    key={state.name}
                    // This does not appear to trigger twice on click
                    // The onfocus handler is for keyboard navigation to access counties
                    onClick={() => handleStateClick(state[stateKey])}
                    onFocus={() => handleStateClick(state[stateKey])}
                >
                    <QESCheckbox
                        checked={editorContext.stateLookup[state[stateKey]].checkboxState === CheckboxStateEnum.Checked}
                        indeterminate={editorContext.stateLookup[state[stateKey]].checkboxState === CheckboxStateEnum.Indeterminate}
                        label={state.name}
                        onChange={() => handleStateCheckboxChange(state[stateKey])}
                        testId={`service-area-editor__state--${state.name}`}
                    />
                </div>
            );
        });
    }, [editorContext.renderedStates, editorContext.selectedState, editorContext.stateLookup, editorContext.countyLookup]);

    const totalStatesSelected = useMemo(() => {
        if (!editorContext.stateLookup)
            return 0;

        return Object.keys(editorContext.stateLookup).filter((state) => editorContext.stateLookup[state].checkboxState !== CheckboxStateEnum.Unchecked).length;
    }, [editorContext.stateLookup]);


    return (
        <ServiceAreaEditorPane
            title="States"
            bulkActionBar={
                <div className="service-area-editor__state-bulk-actions">
                    <QESButton qesType="secondary" qesTestId="service-area-editor__states-select-all" onClick={handleSelectAllStates}>Select All</QESButton>
                    <QESButton qesType="secondary" qesTestId="service-area-editor__states-clear-all" onClick={handleClearAllStates}>Clear All</QESButton>
                </div>
            }
            body={statesList}
            totalSelected={`${totalStatesSelected} of 54`}
            bodyRef={props.statePaneRef}
        />
    );
}

export { ServiceAreaEditorStates };